import { useCallback, useRef, useEffect } from "react";

interface SoundOptions {
  volume?: number;
  debug?: boolean;
}

// Global user interaction state to share across all instances
let globalUserInteracted = false;

// Set up global user interaction detection immediately
const setupGlobalInteractionDetection = (() => {
  let isSetup = false;

  return () => {
    if (isSetup) return;
    isSetup = true;

    const handleGlobalInteraction = () => {
      globalUserInteracted = true;
      document.removeEventListener("click", handleGlobalInteraction, true);
      document.removeEventListener("keydown", handleGlobalInteraction, true);
      document.removeEventListener("touchstart", handleGlobalInteraction, true);
    };

    // Use capture phase to catch interactions before they bubble
    document.addEventListener("click", handleGlobalInteraction, { capture: true, once: true });
    document.addEventListener("keydown", handleGlobalInteraction, { capture: true, once: true });
    document.addEventListener("touchstart", handleGlobalInteraction, { capture: true, once: true });
  };
})();

// Shared audio instances to prevent multiple loading of the same file
const audioCache = new Map<string, HTMLAudioElement>();
const loadingPromises = new Map<string, Promise<HTMLAudioElement>>();

// Create or get shared audio instance
async function getSharedAudio(soundUrl: string, volume: number): Promise<HTMLAudioElement> {
  // Return existing audio if already loaded
  if (audioCache.has(soundUrl)) {
    const audio = audioCache.get(soundUrl)!;
    audio.volume = Math.max(0, Math.min(1, volume));
    return audio;
  }

  // Return existing loading promise if already loading
  if (loadingPromises.has(soundUrl)) {
    const audio = await loadingPromises.get(soundUrl)!;
    audio.volume = Math.max(0, Math.min(1, volume));
    return audio;
  }

  // Create new loading promise
  const loadingPromise = new Promise<HTMLAudioElement>((resolve, reject) => {
    const audio = new Audio();
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.preload = "metadata";

    const handleLoad = () => {
      audioCache.set(soundUrl, audio);
      loadingPromises.delete(soundUrl);
      resolve(audio);
    };

    const handleError = (e: Event) => {
      loadingPromises.delete(soundUrl);
      reject(e);
    };

    audio.addEventListener("canplaythrough", handleLoad, { once: true });
    audio.addEventListener("error", handleError, { once: true });

    audio.src = soundUrl;
  });

  loadingPromises.set(soundUrl, loadingPromise);
  return loadingPromise;
}

// Shared sound hook that uses cached audio instances
export function useSharedSound(soundUrl: string, options: SoundOptions = {}) {
  const { volume = 0.6, debug = false } = options;
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Set up global interaction detection immediately
  useEffect(() => {
    setupGlobalInteractionDetection();
  }, []);

  // Initialize shared audio element
  useEffect(() => {
    if (soundUrl) {
      if (debug) console.log("[useSharedSound] Getting shared audio for:", soundUrl);

      getSharedAudio(soundUrl, volume)
        .then(audio => {
          audioRef.current = audio;
          if (debug) console.log("[useSharedSound] Shared audio ready");
        })
        .catch(error => {
          console.warn("[useSharedSound] Failed to load shared audio:", error);
        });
    }

    // No cleanup needed - audio is shared
    return () => {
      audioRef.current = null;
    };
  }, [soundUrl, volume, debug]);

  const play = useCallback(async () => {
    if (debug) console.log("[useSharedSound] Play requested");

    if (!audioRef.current) {
      if (debug) console.warn("[useSharedSound] Audio not ready yet");
      return;
    }

    // Check global user interaction state
    if (!globalUserInteracted) {
      // Set the flag immediately since we're in a user interaction context
      globalUserInteracted = true;
      if (debug) console.log("[useSharedSound] User interaction detected during play");
    }

    try {
      // Clone the audio for concurrent playback
      const audioClone = audioRef.current.cloneNode() as HTMLAudioElement;
      audioClone.volume = Math.max(0, Math.min(1, volume));
      audioClone.currentTime = 0;

      if (debug) {
        console.log("[useSharedSound] Playing cloned audio:", {
          src: audioClone.src,
          volume: audioClone.volume,
        });
      }

      const playPromise = audioClone.play();

      if (playPromise !== undefined) {
        await playPromise;
        if (debug) console.log("[useSharedSound] Audio played successfully");
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          console.warn("[useSharedSound] Audio autoplay blocked by browser.");
        } else if (error.name === "NotSupportedError") {
          console.warn("[useSharedSound] Audio format not supported.");
        } else {
          console.error("[useSharedSound] Failed to play sound:", error.message);
        }
      } else {
        console.error("[useSharedSound] Failed to play sound:", error);
      }
    }
  }, [volume, debug]);

  // Return array similar to use-sound library for compatibility
  return [play] as const;
}

// For backward compatibility, also export default
export default useSharedSound;
